import { useReducer, useRef } from "react";
import useToggle from "../../../hook/useToggle";
import { notifyError, notifySuccess, notifyWarning } from "../../../services/notification";
import { Action } from "../../../types/reducer-action";
import { ISearchUsersInput } from "../../../types/user";
import AccordionContainer from "../../common/accordion-container";
import { DataEntryEvent } from "../../common/data-entry-reducer";
import { AddIcon, PlayIcon } from "../../common/icons";
import SpinnerOverlay from "../../common/spinner/spinner";
import UserSearchForm from "../../user/user-search-form";
import NewUserModal from "./new-user-modal";
import SecurityUsersTable, { SecurityUsersTableRef } from "./security-users-table";
import './style.scss';
import useUserApi from "./useUserApi";
import UserDetailsPane from "./user-details-pane";
import { UserContext, UserDispatchContext, UserProfileState, useUserDispatch, useUserState, userProfileReducer } from "./user-manager-context";
import Button from "../../common/button";
import confirm from "../../common/confirm";
import useAuthToken from "../../../hook/useAuthToken";

const MAX_RESULTS = 700; // hard cap on backend

export default function UserManager() {
  const [state, dispatch] = useReducer(userProfileReducer, {} as UserProfileState);  

  return (
    <UserContext.Provider value={state}>
      <UserDispatchContext.Provider value={dispatch}>
        <UserActionPane />

        <UserSearchPane />

        <UserDetailsPane />
      </UserDispatchContext.Provider>
    </UserContext.Provider>
  );
}

function UserActionPane() {
  const [getAccessToken] = useAuthToken();
  const dispatch = useUserDispatch();

  const { isOpen: isCreationModalOpen, toggle: toggleCreationModal } = useToggle();

  const handleNew = () => {
    dispatch(new Action(DataEntryEvent.NewForm, { assignedRoles: [] }));
    toggleCreationModal();
  };

  const handleRenable = () => {
    confirm({
        title: 'Re-enable Users?',
        content: 'Are you sure you want to re-enable the user accounts disabled during the deployment?',
        okText: <><PlayIcon style={{ color: 'unset' }}/> Re-enable</>,
        okBtnColor: 'primary'
    }).then(async () => {
      const token = await getAccessToken();
      const response = await fetch('/api/security/reenable-users', { method: "POST", headers: { "Authorization": `Bearer ${token}` }});
      if (response.ok) {
        notifySuccess('Request completed');
      }
      else {
        notifyError('There was an error attempting to re-enable users. Please contact OMS operations team.');
      }
    });
  }

  return (
    <div className="col-auto oms-row col">
      <Button type="button" color="primary" className="action-link"
        onClick={handleNew}>
        <AddIcon /> Create New User
      </Button>

      <Button type="button" color="secondary" className="action-link"
        onClick={handleRenable}>
        <PlayIcon style={{ color: 'unset' }}/> Re-enable Users
      </Button>

      <NewUserModal isOpen={isCreationModalOpen} toggle={toggleCreationModal} />
    </div>
  );
}

function UserSearchPane() {
  const { dataSet } = useUserState();
  const dispatch = useUserDispatch();

  const { searchUsers, busy } = useUserApi();

  const usersTableRef = useRef<SecurityUsersTableRef>(null);

  const handleSubmit = (input: ISearchUsersInput) =>
    searchUsers(input)
      .then(results => results && results.length === MAX_RESULTS &&
        notifyWarning(`The system has reached the maximum limit of ${MAX_RESULTS} records, please refine your search and try again.`, 5000)
      );

  const handleReset = () => dispatch(new Action(DataEntryEvent.NewDataSet));

  return (
    <AccordionContainer title="Search Users">
      <UserSearchForm onSubmit={handleSubmit} onReset={handleReset}
        onExport={() => usersTableRef.current?.export()}
        hasData={dataSet && dataSet.length > 0} busy={busy} admin />

      <SecurityUsersTable pageSize={10} ref={usersTableRef} />

      {busy && <SpinnerOverlay />}
    </AccordionContainer>
  );
}
